import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Chip,
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Shield as ShieldIcon,
} from "@mui/icons-material";
import { routes } from "@/constants/routes";

export default function SetupDiagnostics() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [ack1, setAck1] = useState(false);
  const [ack2, setAck2] = useState(false);
  const [ack3, setAck3] = useState(false);

  // Redirect if setup already complete
  useEffect(() => {
    if (localStorage.getItem("setup_complete") === "true") {
      navigate(routes.dashboard, { replace: true });
    }
  }, [navigate]);

  // Diagnostics
  const localStorageOk = (() => {
    try {
      localStorage.setItem("_diag_test", "1");
      localStorage.removeItem("_diag_test");
      return true;
    } catch {
      return false;
    }
  })();

  const cryptoOk = typeof crypto?.subtle !== "undefined";
  const sessionStorageOk = (() => {
    try {
      sessionStorage.setItem("_diag_test", "1");
      sessionStorage.removeItem("_diag_test");
      return true;
    } catch {
      return false;
    }
  })();

  const checks = [
    { label: t("setup.localStorage", "localStorage"), ok: localStorageOk },
    {
      label: t("setup.sessionStorage", "sessionStorage"),
      ok: sessionStorageOk,
    },
    { label: t("setup.webCrypto", "Web Crypto API"), ok: cryptoOk },
  ];

  const passCount = checks.filter((c) => c.ok).length;
  const healthScore = Math.round((passCount / checks.length) * 100);
  const allAcknowledged = ack1 && ack2 && ack3;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <ShieldIcon color="primary" />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
          }}
        >
          {t("setup.diagnosticsTitle", "Setup Diagnostics")}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 3,
        }}
      >
        {t(
          "setup.diagnosticsDesc",
          "Let's make sure your browser supports all features required for secure image storage.",
        )}
      </Typography>
      {/* Health score */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              color: "text.secondary",
            }}
          >
            {t("setup.healthScore", "Browser Health Score")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <LinearProgress
              variant="determinate"
              value={healthScore}
              color={healthScore === 100 ? "success" : "warning"}
              sx={{ flexGrow: 1, height: 12, borderRadius: 6 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
              }}
            >
              {healthScore}%
            </Typography>
          </Box>
        </CardContent>
      </Card>
      {/* Checks */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("setup.checks", "System Checks")}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {checks.map((check, i) => (
              <Box
                key={i}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                {check.ok ? (
                  <CheckIcon color="success" />
                ) : (
                  <WarningIcon color="error" />
                )}
                <Typography variant="body1">{check.label}</Typography>
                <Chip
                  label={
                    check.ok ? t("setup.pass", "Pass") : t("setup.fail", "Fail")
                  }
                  size="small"
                  color={check.ok ? "success" : "error"}
                />
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
      {/* Acknowledgments */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("setup.acknowledge", "Please acknowledge the following")}
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={ack1}
                onChange={(e) => setAck1(e.target.checked)}
              />
            }
            label={t(
              "setup.ack1",
              "I understand that encryption keys are stored only in this browser",
            )}
            sx={{ display: "block", mb: 1 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={ack2}
                onChange={(e) => setAck2(e.target.checked)}
              />
            }
            label={t(
              "setup.ack2",
              "I understand that clearing browser data will permanently remove my keys",
            )}
            sx={{ display: "block", mb: 1 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={ack3}
                onChange={(e) => setAck3(e.target.checked)}
              />
            }
            label={t(
              "setup.ack3",
              "I will export my key bundle regularly as a backup",
            )}
            sx={{ display: "block" }}
          />
        </CardContent>
      </Card>
      <Button
        variant="contained"
        size="large"
        fullWidth
        disabled={!allAcknowledged}
        onClick={() => navigate(routes.setupKeys)}
      >
        {t("setup.continue", "Continue to Key Setup")}
      </Button>
    </Box>
  );
}
